import { useMemo, useState } from 'react';
import { Bot, ChevronRight, Home, Loader2, MousePointerClick, RefreshCw, Search, Wrench } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import EmptyState from '@/components/ui/EmptyState';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import {
  CANAL_LABELS,
  CLIC_LABELS,
  HERRAMIENTA_LABELS,
  type CanalSerena,
  type ConversacionSerenaDetalle,
  type ConversacionSerenaResumen,
} from '@/lib/serena';

/**
 * Las conversaciones con Serena, el asistente de tusereno.es: la burbuja de
 * chat de toda la web y la barra de la búsqueda de /alojamientos. Para ver
 * qué pregunta la gente y dónde falla Serena sin ir a Cosmos.
 *
 * Solo hay los últimos 30 días: la web las borra solas pasado ese tiempo. Las
 * llamadas de teléfono comparten contenedor, pero están en pruebas y no salen.
 */

function classNames(...c: (string | false | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function cuando(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Madrid',
  });
}

const FILTROS: { key: 'todas' | CanalSerena; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'burbuja', label: 'Burbuja' },
  { key: 'alojamientos', label: 'Alojamientos' },
];

function CanalBadge({ canal }: { canal: CanalSerena }) {
  return (
    <span
      className={classNames(
        'badge',
        canal === 'alojamientos' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700',
      )}
    >
      {CANAL_LABELS[canal]}
    </span>
  );
}

function Herramientas({ nombres }: { nombres: string[] }) {
  if (!nombres.length) return <span className="text-sereno-dark/30">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {nombres.map((n) => (
        <span key={n} className="badge bg-slate-100 text-slate-600">
          {HERRAMIENTA_LABELS[n] ?? n}
        </span>
      ))}
    </div>
  );
}

function Cifra({ titulo, valor, detalle }: { titulo: string; valor: number | string; detalle?: string }) {
  return (
    <div className="card py-3">
      <p className="text-xs text-sereno-dark/50">{titulo}</p>
      <p className="text-2xl font-bold mt-0.5">{valor}</p>
      {detalle && <p className="text-xs text-sereno-dark/40 mt-0.5">{detalle}</p>}
    </div>
  );
}

/** La conversación entera: el visitante a la derecha, Serena a la izquierda y lo que consultó, plegado. */
function Conversacion({ c }: { c: ConversacionSerenaDetalle }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-sereno-dark/50">
        <CanalBadge canal={c.canal} />
        <span>{c.pagina}</span>
        <span>·</span>
        <span>
          {cuando(c.creada)} → {cuando(c.actualizada)}
        </span>
        {c.locale === 'en' && <span className="badge bg-amber-100 text-amber-700">Inglés</span>}
      </div>

      <div className="space-y-2">
        {c.turnos.map((t, i) =>
          t.tipo === 'visitante' ? (
            <div key={i} className="flex justify-end">
              <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-sereno-dark px-3 py-2 text-sm text-white whitespace-pre-wrap">
                {t.texto}
              </p>
            </div>
          ) : t.tipo === 'serena' ? (
            <div key={i} className="flex">
              <p className="max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-sm whitespace-pre-wrap dark:bg-slate-800">
                {t.texto}
              </p>
            </div>
          ) : (
            <details key={i} className="ml-2 text-xs text-sereno-dark/50">
              <summary className="cursor-pointer select-none">
                <Wrench size={12} className="inline mr-1" />
                {HERRAMIENTA_LABELS[t.nombre] ?? t.nombre}
                {t.argumentos && t.argumentos !== '{}' && <span className="ml-1 font-mono">{t.argumentos.slice(0, 120)}</span>}
              </summary>
              {t.resultado && (
                <pre className="mt-1 max-h-60 overflow-auto rounded-lg bg-slate-50 p-2 font-mono text-[11px] whitespace-pre-wrap break-all dark:bg-slate-900">
                  {t.resultado}
                </pre>
              )}
            </details>
          ),
        )}
      </div>

      {c.clicsDetalle.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs font-medium text-sereno-dark/60 mb-1">Después pulsó</p>
          <ul className="text-sm space-y-0.5">
            {c.clicsDetalle.map((k, i) => (
              <li key={i}>
                {CLIC_LABELS[k.accion] ?? k.accion}
                {k.id && <span className="text-sereno-dark/40"> · casa {k.id}</span>}
                <span className="text-sereno-dark/40"> · {cuando(k.en)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function SerenaPage() {
  const [filtro, setFiltro] = useState<'todas' | CanalSerena>('todas');
  const [busqueda, setBusqueda] = useState('');
  const [abierta, setAbierta] = useState<ConversacionSerenaDetalle | null>(null);
  const [abriendo, setAbriendo] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  const { data, loading, error, refresh } = useApi<ConversacionSerenaResumen[]>(() => api.getConversacionesSerena(), []);
  const todas = useMemo(() => data ?? [], [data]);

  const cifras = useMemo(
    () => ({
      conversaciones: todas.length,
      mensajes: todas.reduce((n, c) => n + c.turnos, 0),
      conClic: todas.filter((c) => c.clics > 0).length,
      propietarios: todas.filter((c) => c.propietario).length,
    }),
    [todas],
  );

  const contadores = useMemo(() => {
    const acc: Record<string, number> = { todas: todas.length };
    for (const c of todas) acc[c.canal] = (acc[c.canal] || 0) + 1;
    return acc;
  }, [todas]);

  const visibles = useMemo(() => {
    const q = normalizar(busqueda.trim());
    return todas
      .filter((c) => filtro === 'todas' || c.canal === filtro)
      .filter((c) => !q || normalizar(`${c.primerMensaje} ${c.pagina ?? ''}`).includes(q));
  }, [todas, filtro, busqueda]);

  const abrir = async (id: string) => {
    setAbriendo(id);
    setFallo(null);
    try {
      setAbierta(await api.getConversacionSerena(id));
    } catch (e) {
      setFallo(e instanceof Error ? e.message : 'No se pudo abrir la conversación');
    } finally {
      setAbriendo(null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Bot size={24} className="text-sereno-dark shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">Serena</h1>
            <p className="text-sm text-sereno-dark/40 mt-0.5">
              Conversaciones del chat de tusereno.es de los últimos 30 días.
            </p>
          </div>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="btn-ghost flex items-center gap-2 text-sm shrink-0"
          title="Recargar la lista"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Cifra titulo="Conversaciones" valor={cifras.conversaciones} />
        <Cifra titulo="Mensajes de visitantes" valor={cifras.mensajes} />
        <Cifra
          titulo="Pulsaron una tarjeta o casa"
          valor={cifras.conClic}
          detalle="Se apunta desde el 21-10-2026"
        />
        <Cifra titulo="Propietarios" valor={cifras.propietarios} detalle="Llevados al formulario" />
      </div>

      <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto mb-4">
        <div className="flex gap-2 min-w-max">
          {FILTROS.map((f) => {
            const activo = filtro === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFiltro(f.key)}
                aria-pressed={activo}
                className={classNames(
                  'h-11 px-4 rounded-xl border-2 text-sm font-medium transition-colors whitespace-nowrap',
                  activo ? 'border-sereno-dark bg-sereno-dark text-white' : 'border-gray-100 bg-white hover:border-slate-300',
                )}
              >
                {f.label}
                <span className={classNames('ml-1.5', activo ? 'opacity-70' : 'text-sereno-dark/40')}>
                  {contadores[f.key] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar en el primer mensaje o la página"
          className="input pl-9 h-11 w-full"
        />
      </div>

      {loading && !data && <LoadingSpinner />}

      {(error || fallo) && (
        <div className="card bg-red-50 border-red-200 text-red-700 text-sm mb-4">{error ?? fallo}</div>
      )}

      {!loading && !error && visibles.length === 0 && (
        <EmptyState
          title={busqueda || filtro !== 'todas' ? 'Nada por aquí' : 'Todavía no hay conversaciones'}
          description={
            busqueda || filtro !== 'todas'
              ? 'Prueba con otro filtro o cambia la búsqueda.'
              : 'Cuando alguien escriba a Serena en la web, aparecerá aquí.'
          }
        />
      )}

      {visibles.length > 0 && (
        <>
          {/* Tarjetas (móvil) */}
          <div className="space-y-3 md:hidden">
            {visibles.map((c) => (
              <button key={c.id} onClick={() => abrir(c.id)} className="card block w-full text-left active:bg-slate-50">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium line-clamp-2">{c.primerMensaje || '(sin texto)'}</p>
                  {abriendo === c.id ? <Loader2 size={16} className="animate-spin shrink-0" /> : <CanalBadge canal={c.canal} />}
                </div>
                <p className="text-xs text-sereno-dark/40 mt-1.5">
                  {cuando(c.actualizada)} · {c.turnos} {c.turnos === 1 ? 'mensaje' : 'mensajes'} · {c.pagina}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Herramientas nombres={c.herramientas} />
                  {c.clics > 0 && (
                    <span className="badge bg-emerald-100 text-emerald-700">
                      <MousePointerClick size={12} className="inline mr-0.5" />
                      {c.clics}
                    </span>
                  )}
                  {c.propietario && (
                    <span className="badge bg-amber-100 text-amber-700">
                      <Home size={12} className="inline mr-0.5" />
                      Propietario
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Tabla (escritorio) */}
          <div className="card overflow-x-auto hidden md:block p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-sereno-dark/50">
                  <th className="px-4 py-3 font-medium">Último mensaje</th>
                  <th className="px-4 py-3 font-medium">Dónde</th>
                  <th className="px-4 py-3 font-medium">Primer mensaje</th>
                  <th className="px-4 py-3 font-medium">Mensajes</th>
                  <th className="px-4 py-3 font-medium">Qué hizo Serena</th>
                  <th className="px-4 py-3 font-medium">Clics</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {visibles.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => abrir(c.id)}
                    className="border-b border-gray-50 last:border-0 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sereno-dark/60">{cuando(c.actualizada)}</td>
                    <td className="px-4 py-3">
                      <CanalBadge canal={c.canal} />
                      <div className="text-xs text-sereno-dark/40 mt-1 max-w-[180px] truncate" title={c.pagina}>
                        {c.pagina}
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <span className="line-clamp-2">{c.primerMensaje || '(sin texto)'}</span>
                      {c.propietario && <span className="badge bg-amber-100 text-amber-700 mt-1">Propietario</span>}
                    </td>
                    <td className="px-4 py-3">{c.turnos}</td>
                    <td className="px-4 py-3">
                      <Herramientas nombres={c.herramientas} />
                    </td>
                    <td className="px-4 py-3">{c.clics || <span className="text-sereno-dark/30">—</span>}</td>
                    <td className="px-4 py-3 text-right">
                      {abriendo === c.id ? (
                        <Loader2 size={16} className="animate-spin inline" />
                      ) : (
                        <ChevronRight size={16} className="inline text-sereno-dark/40" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal isOpen={Boolean(abierta)} onClose={() => setAbierta(null)} title="Conversación con Serena" size="lg">
        {abierta && <Conversacion c={abierta} />}
      </Modal>
    </div>
  );
}
